import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  Divider,
  Autocomplete,
  CircularProgress,
  Avatar,
  Card,
  CardContent,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Timer as TimerIcon,
  Check as CheckIcon,
  Construction as ConstructionIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Build as BuildIcon,
  Refresh as RefreshIcon,
  Send as SendIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import MaintenanceDetails from './MaintenanceDetails';
import QuoteDialog from './QuoteDialog';
import QuoteViewDialog from './QuoteViewDialog';
import { getProductImage } from '../../utils/productUtils';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import logger from '../../utils/logger';
import { openMaintenanceReviewWhatsApp } from '../../utils/whatsappTemplates';

const MaintenanceRepair = ({ open, handleClose, maintenanceId, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [timerRunning, setTimerRunning] = useState(false);
  const [laborTime, setLaborTime] = useState(0); // in seconds
  const [notes, setNotes] = useState('');
  const [hasUnreadNote, setHasUnreadNote] = useState(false);
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('Normale');
  const [mechanics, setMechanics] = useState([]);
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  const [workSessions, setWorkSessions] = useState([]);
  const [currentWorkSession, setCurrentWorkSession] = useState(null);
  const [busyMechanicIds, setBusyMechanicIds] = useState(new Set());
  const [userCompanyId, setUserCompanyId] = useState(null);
  const timerRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showDetails, setShowDetails] = useState(false);
  const [errors, setErrors] = useState({});
  const [openCustomWorkDialog, setOpenCustomWorkDialog] = useState(false);
  const [customWork, setCustomWork] = useState({
    description: '',
    materialsCost: '',
    sellingPrice: ''
  });
  const [customWorkErrors, setCustomWorkErrors] = useState({});
  const [openQuoteDialog, setOpenQuoteDialog] = useState(false);
  const [openQuoteViewDialog, setOpenQuoteViewDialog] = useState(false);
  const [hasQuotes, setHasQuotes] = useState(false);
  const [openAdvancedSearch, setOpenAdvancedSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [editingQuantities, setEditingQuantities] = useState({});
  const [discount, setDiscount] = useState(0);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedMaintenanceId, setSelectedMaintenanceId] = useState(null);
  const [openMaintenanceDetails, setOpenMaintenanceDetails] = useState(false);
  const [discountInput, setDiscountInput] = useState('');

  useEffect(() => {
    const initializeData = async () => {
      if (open && maintenanceId) {
        try {
          // Controlla se ci sono preventivi per questa manutenzione
          await checkForQuotes();
          // Prima ottieni il companyId dell'utente
          const userResponse = await api.get('/auth/me');
          const companyId = userResponse.data.companyId;
          setUserCompanyId(companyId);
          
          logger.debug('🚀 Inizializzazione dati per manutenzione:', maintenanceId);
          
          // Carica i meccanici e gli altri dati
          await Promise.all([
            fetchMaintenance(),
            fetchProducts(),
            fetchMechanics(),
            fetchBusyMechanics(),
            fetchWorkSessions(),
            fetchMaintenanceItems()
          ]);
          
          logger.debug('✅ Inizializzazione completata');
        } catch (err) {
          logger.error('Errore durante l\'inizializzazione:', err);
          setSnackbar({
            open: true,
            message: "Errore durante il caricamento dei dati. Riprova più tardi.",
            severity: 'error'
          });
        }
      }
    };

    initializeData();

    return () => {
      const currentTimerRef = timerRef.current;
      if (currentTimerRef) {
        clearInterval(timerRef.current);
      }
    };
  }, [open, maintenanceId]);

  // Add useEffect to update laborTime when workSessions change
  useEffect(() => {
    if (workSessions.length > 0) {
      // Il calcolo dettagliato è già gestito in fetchWorkSessions
      // Qui aggiungiamo solo un aggiornamento automatico per le sessioni attive
      if (timerRunning && workSessions.some(s => !s.endTime)) {
        // Crea timer per aggiornare il contatore delle sessioni attive
        timerRef.current = setInterval(() => {
          const now = new Date();
          const updatedTime = workSessions.reduce((total, session) => {
            if (session.duration) {
              // Per sessioni completate, usa la durata calcolata
              return total + session.duration;
            } else if (session.startTime && !session.endTime) {
              // Per sessioni attive, calcola il tempo trascorso fino ad ora
              const startTime = new Date(session.startTime);
              const duration = Math.floor((now - startTime) / 1000);
              return total + duration;
            } else if (session.startTime && session.endTime) {
              // Per sessioni con inizio e fine, calcola la durata
              const startTime = new Date(session.startTime);
              const endTime = new Date(session.endTime);
              return total + Math.floor((endTime - startTime) / 1000);
            }
            return total;
          }, 0);
          
          setLaborTime(updatedTime);
        }, 1000);
        
        return () => {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
        };
      }
    }
  }, [workSessions, timerRunning]);

  const fetchMaintenance = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/maintenance/${maintenanceId}`);
      if (res.data) {
        setMaintenance(res.data);
        setStatus(res.data.status || '');
        setPriority(res.data.priority || 'Normale');
        setNotes(res.data.privateNotes || '');
        setHasUnreadNote(!!res.data.hasUnreadNote);
        // Convert laborTime from minutes to seconds for storage
        setLaborTime((res.data.laborTime || 0) * 60);
        // Carica lo sconto
        setDiscount(parseFloat(res.data.discount) || 0);
        setDiscountInput(res.data.discount ? parseFloat(res.data.discount).toFixed(2) : '');
      } else {
        logger.error('Formato risposta API manutenzione non valido');
        // Valori predefiniti
        setMaintenance(null);
        setStatus('');
        setPriority('Normale');
        setNotes('');
        setHasUnreadNote(false);
        setLaborTime(0);
      }
    } catch (err) {
      logger.error('Error fetching maintenance:', err);
      // Valori predefiniti in caso di errore
      setMaintenance(null);
      setStatus('');
      setNotes('');
      setHasUnreadNote(false);
      setLaborTime(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get('/products', {
        params: { all: 'true' } // Richiedi tutti i prodotti per le manutenzioni
      });
      // Con all=true, la risposta è direttamente un array
      let allProducts = [];
      if (Array.isArray(res.data)) {
        allProducts = res.data;
      } else if (res.data && res.data.products) {
        allProducts = res.data.products;
      } else {
        logger.error('Formato risposta API prodotti non valido');
        setProducts([]);
        return;
      }
      
      // Filtra i prodotti per mostrare solo quelli con scorte > 0 o che sono servizi di manodopera
      const filteredProducts = allProducts.filter(product => 
        product.isLabor || (product.stock > 0)
      );
      setProducts(filteredProducts);
    } catch (err) {
      logger.error('Error fetching products:', err);
      setProducts([]);
    }
  };

  const fetchWorkSessions = async () => {
    try {
      logger.debug('🔍 Recupero sessioni di lavoro per la manutenzione:', maintenanceId);
      const res = await api.get(`/work-sessions/maintenance/${maintenanceId}`);
      
      if (Array.isArray(res.data)) {
        logger.debug(`✅ Trovate ${res.data.length} sessioni di lavoro`);
        
        setWorkSessions(res.data);
        
        // Calcola immediatamente il tempo totale di lavoro per aggiornare l'interfaccia
        const now = new Date();
        const totalDuration = res.data.reduce((total, session) => {
          if (session.duration) {
            return total + session.duration;
          } else if (session.startTime && !session.endTime) {
            const startTime = new Date(session.startTime);
            const duration = Math.floor((now - startTime) / 1000);
            return total + duration;
          } else if (session.startTime && session.endTime) {
            const startTime = new Date(session.startTime);
            const endTime = new Date(session.endTime);
            const duration = Math.floor((endTime - startTime) / 1000);
            return total + duration;
          }
          return total;
        }, 0);
        
        logger.debug('⏱️ Tempo totale calcolato (secondi):', totalDuration);
        setLaborTime(totalDuration);
        
        // Più sessioni attive possono coesistere nella stessa manutenzione
        const activeSessions = res.data.filter(session => session.endTime === null);
        setTimerRunning(activeSessions.length > 0);
        setCurrentWorkSession(null);
      } else {
        logger.error('❌ Formato risposta API sessioni di lavoro non valido');
        setWorkSessions([]);
        setCurrentWorkSession(null);
        setTimerRunning(false);
      }
    } catch (err) {
      logger.error('❌ Errore nel caricamento delle sessioni di lavoro:', err);
      setWorkSessions([]);
      setCurrentWorkSession(null);
      setTimerRunning(false);
    }
  };

  const fetchMaintenanceItems = async () => {
    try {
      const res = await api.get(`/maintenance-items/maintenance/${maintenanceId}`);
      if (Array.isArray(res.data)) {
        // Convert maintenance items to the format used by selectedProducts
        const formattedItems = res.data.map(item => {
          // Verifica se si tratta di un articolo personalizzato
          if (item.customItem) {
            return {
              id: `custom-${item.id}`,  // ID univoco per articoli personalizzati
              code: item.customCode || 'ART-CUST',
              name: item.customDescription || 'Articolo personalizzato',
              purchasePrice: item.customPurchasePrice || 0,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discount: item.discount,
              isLabor: item.isLabor,
              maintenanceItemId: item.id,
              includesLabor: item.includesLabor,
              isCustomItem: true  // Flag per identificare articoli personalizzati nella UI
            };
          }
          
          // Articolo normale da prodotto esistente
          return {
            ...item.Product,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            discount: item.discount,
            isLabor: item.isLabor,
            maintenanceItemId: item.id,
            includesLabor: item.includesLabor
          };
        });
        
        logger.debug('📋 Articoli formattati:', formattedItems.length);
        setSelectedProducts(formattedItems);
      } else {
        logger.error('Formato risposta API articoli manutenzione non valido');
        setSelectedProducts([]);
      }
    } catch (err) {
      logger.error('Error fetching maintenance items:', err);
      setSelectedProducts([]);
    }
  };

  const startTimer = async () => {
    if (!selectedMechanic) {
      alert('Seleziona un meccanico prima di avviare il timer');
      return;
    }

    try {
      // Verifica che l'ID del meccanico sia valido
      const mechanicId = selectedMechanic.id; // Usiamo l'ID del meccanico (non l'ID utente)
      if (!mechanicId) {
        throw new Error('ID meccanico non valido');
      }
      
      logger.debug('📌 Avvio sessione di lavoro per manutenzione:', maintenanceId);
      
      // Start a new work session
      const res = await api.post('/work-sessions/start', {
        maintenanceId,
        mechanicId: mechanicId,
        notes: 'Sessione di lavoro avviata'
      });

      logger.debug('✅ Sessione di lavoro avviata con successo');
      setStatus('In Corso'); // Automatically set status to 'In Corso'
      
      // Refresh work sessions
      fetchWorkSessions();
      fetchBusyMechanics();
    } catch (err) {
      logger.error('Error starting work session:', err);
      logger.error('Dettagli errore:', err.response?.data || 'Nessun dettaglio disponibile');
      alert('Errore nell\'avvio della sessione di lavoro: ' + (err.response?.data?.msg || err.message));
    }
  };

  const pauseTimer = async () => {
    if (!currentWorkSession) return;

    try {
      logger.debug('🔍 Terminazione sessione di lavoro:', currentWorkSession.id);
      // Pause the current work session
      const result = await api.put(`/work-sessions/${currentWorkSession.id}/pause`, {
        notes: 'Sessione di lavoro in pausa'
      });
      
      logger.debug('✅ Sessione terminata con successo');

      // Aggiorna lo stato dell'interfaccia
      setTimerRunning(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Imposta correttamente a null la sessione corrente
      setCurrentWorkSession(null);
      
      // Aggiorna i dati delle sessioni
      await fetchWorkSessions();
      
      // Mostra conferma all'utente
      setSnackbar({
        open: true,
        message: 'Sessione di lavoro terminata con successo',
        severity: 'success'
      });
    } catch (err) {
      logger.error('❌ Errore nella terminazione della sessione di lavoro:', err);
      setSnackbar({
        open: true,
        message: 'Errore nella terminazione della sessione di lavoro',
        severity: 'error'
      });
    }
  };

  const endWorkSession = async (sessionId) => {
    if (!sessionId) return;
    try {
      await api.put(`/work-sessions/${sessionId}/pause`, { notes: 'Sessione terminata' });
      setSnackbar({ open: true, message: 'Sessione terminata con successo', severity: 'success' });
      fetchWorkSessions();
      fetchBusyMechanics();
    } catch (err) {
      logger.error('❌ Errore nella terminazione della sessione di lavoro:', err);
      setSnackbar({ open: true, message: err.response?.data?.msg || 'Errore nella terminazione della sessione di lavoro', severity: 'error' });
    }
  };

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const [includesLabor, setIncludesLabor] = useState(true);

  const handleAddProduct = async () => {
    if (!selectedProduct) return;
    
    // Valida la quantità
    const quantity = productQuantity === '' || productQuantity < 1 ? 1 : productQuantity;
    
    try {
      // Add product to maintenance items via API
      // When includesLabor is true, the API will use sellingPriceWithAssembly instead of sellingPrice
      await api.post('/maintenance-items', {
        maintenanceId,
        productId: selectedProduct.id,
        quantity: quantity,
        unitPrice: null, // Let the server determine the price based on includesLabor flag
        isLabor: selectedProduct.isLabor,
        includesLabor: includesLabor
      });
      
      // Refresh maintenance items
      fetchMaintenanceItems();
      
      // Reset selection
      setSelectedProduct(null);
      setProductQuantity(1);
      setIncludesLabor(true); // Keep includesLabor true for next product
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Prodotto aggiunto con successo',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error adding product to maintenance:', err);
      
      // Show more specific error message if available
      let errorMessage = 'Errore nell\'aggiunta del prodotto';
      if (err.response && err.response.data && err.response.data.msg) {
        errorMessage = err.response.data.msg;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleRemoveProduct = async (maintenanceItemId) => {
    try {
      // Remove product from maintenance items via API
      await api.delete(`/maintenance-items/${maintenanceItemId}`);
      
      // Refresh maintenance items
      fetchMaintenanceItems();
    } catch (err) {
      console.error('Error removing product from maintenance:', err);
      alert('Errore nella rimozione del prodotto');
    }
  };

  const handleQuantityChange = (maintenanceItemId, value) => {
    // Permetti valori vuoti temporaneamente durante la digitazione
    setEditingQuantities(prev => ({
      ...prev,
      [maintenanceItemId]: value === '' ? '' : value
    }));
  };

  // Funzione per caricare lo storico manutenzioni del cliente
  const fetchMaintenanceHistory = async (customerId) => {
    if (!customerId) return;
    setLoadingHistory(true);
    try {
      const res = await api.get(`/maintenance/customer/${customerId}`);
      const history = res.data || [];
      // Ordina per data (più recente per prima)
      const sortedHistory = history.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.startDate || 0);
        const dateB = new Date(b.createdAt || b.startDate || 0);
        return dateB - dateA; // Ordine decrescente (più recente per prima)
      });
      setMaintenanceHistory(sortedHistory);
    } catch (err) {
      console.error('Error fetching maintenance history:', err);
      setMaintenanceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Funzione per aprire il dialog dello storico manutenzioni
  const handleOpenHistoryDialog = () => {
    if (maintenance?.Customer?.id) {
      fetchMaintenanceHistory(maintenance.Customer.id);
      setOpenHistoryDialog(true);
    }
  };

  // Funzione per chiudere il dialog dello storico manutenzioni
  const handleCloseHistoryDialog = () => {
    setOpenHistoryDialog(false);
    setMaintenanceHistory([]);
  };

  // Funzione per gestire il click su una manutenzione nello storico
  const handleMaintenanceClick = (maintenanceId) => {
    setSelectedMaintenanceId(maintenanceId);
    setOpenMaintenanceDetails(true);
  };

  // Funzione per chiudere il dialog dei dettagli manutenzione
  const handleCloseMaintenanceDetails = () => {
    setOpenMaintenanceDetails(false);
    setSelectedMaintenanceId(null);
  };

  // Funzione per formattare la data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data non valida';
      }
      return format(date, 'dd/MM/yyyy', { locale: it });
    } catch (error) {
      return 'Data non valida';
    }
  };

  // Funzione per ottenere il colore dello stato
  const getStatusColor = (status) => {
    const statusColors = {
      'In Coda': 'default',
      'In Corso': 'primary',
      'Attesa Ricambi': 'warning',
      'Attesa Cliente': 'info',
      'Da Chiamare': 'secondary',
      'Da Ritirare': 'success',
      'Sollecitato': 'error',
      'Parcheggio': 'default',
      'Consegnato': 'success'
    };
    return statusColors[status] || 'default';
  };

  const handleQuantityBlur = async (maintenanceItemId, value) => {
    // Quando l'utente esce dal campo, valida e aggiorna
    const quantity = value === '' ? 1 : parseInt(value) || 1;
    
    // Rimuovi il valore temporaneo dallo stato di editing
    setEditingQuantities(prev => {
      const newState = { ...prev };
      delete newState[maintenanceItemId];
      return newState;
    });

    // Trova il prodotto corrente per verificare se la quantità è cambiata
    const product = selectedProducts.find(p => p.maintenanceItemId === maintenanceItemId);
    if (!product || product.quantity === quantity) {
      return; // Nessun cambiamento necessario
    }

    try {
      await api.put(`/maintenance-items/${maintenanceItemId}`, {
        quantity: quantity
      });
      
      // Ricarica i dati
      fetchMaintenanceItems();
      
      setSnackbar({
        open: true,
        message: 'Quantità aggiornata con successo',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating quantity:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.msg || 'Errore nell\'aggiornamento della quantità',
        severity: 'error'
      });
      // Ricarica i dati per ripristinare il valore originale
      fetchMaintenanceItems();
    }
  };

  const calculateTotalCost = () => {
    return selectedProducts.reduce((total, product) => {
      const unitPrice = parseFloat(product.unitPrice) || 0;
      const quantity = product.quantity || 0;
      const discount = parseFloat(product.discount) || 0; // Sconto in euro invece che percentuale
      const itemTotal = unitPrice * quantity;
      const discountAmount = discount; // Sconto già in euro
      return total + (itemTotal - discountAmount);
    }, 0);
  };

  const calculateLaborCost = () => {
    // Se la manutenzione è consegnata, usa il valore salvato
    if (maintenance.status === 'Consegnato' && maintenance.laborCost !== undefined) {
      return parseFloat(maintenance.laborCost);
    }

    if (!workSessions || workSessions.length === 0) {
      return 0;
    }
    
    // Calcola il costo totale sommando il total_cost di tutte le sessioni
    const totalCost = workSessions.reduce((total, session) => {
      const sessionCost = parseFloat(session.total_cost || session.totalCost || 0);
      return total + sessionCost;
    }, 0);
    
    return totalCost;
  };

  const calculateMaterialsCost = () => {
    // Se la manutenzione è consegnata, usa il valore salvato + IVA 22%
    if (maintenance.status === 'Consegnato' && maintenance.materialsCost !== undefined) {
      return parseFloat(maintenance.materialsCost) * 1.22;
    }

    // Calculate cost of all products that are not labor using purchase price + IVA 22%
    if (!Array.isArray(selectedProducts)) {
      return 0;
    }
    return selectedProducts.reduce((total, product) => {
      if (!product.isLabor) {
        if (product.customPurchasePrice != null) {
          return total + (parseFloat(product.customPurchasePrice) * product.quantity * 1.22);
        }
        if (product.purchasePrice != null) {
          return total + (parseFloat(product.purchasePrice) * product.quantity * 1.22);
        }
      }
      return total;
    }, 0);
  };
  
  const calculateOverheadCost = () => {
    // Se la manutenzione è consegnata, usa il valore salvato + IVA 22%
    if (maintenance.status === 'Consegnato' && maintenance.overheadCost !== undefined) {
      return parseFloat(maintenance.overheadCost) * 1.22;
    }

    // Calculate 20% of labor + materials cost
    const laborCost = calculateLaborCost();
    const materialsCost = calculateMaterialsCost();
    return (laborCost + materialsCost) * 0.2;
  };
  
  const calculateGrandTotal = () => {
    // Se la manutenzione è consegnata, usa il valore salvato + IVA 22%
    if (maintenance.status === 'Consegnato' && maintenance.totalCost !== undefined) {
      return parseFloat(maintenance.totalCost) * 1.22;
    }

    // Calculate total cost including labor, materials, and overhead
    // NON sottrarre lo sconto qui perché sono costi
    const laborCost = calculateLaborCost();
    const materialsCost = calculateMaterialsCost();
    const overheadCost = calculateOverheadCost();
    return laborCost + materialsCost + overheadCost;
  };

  const handleSendNote = async () => {
    try {
      await api.put(`/maintenance/${maintenanceId}`, {
        privateNotes: notes,
        hasUnreadNote: true
      });
      setHasUnreadNote(true);
      setSnackbar({ open: true, message: 'Nota inviata. Verrà mostrata l\'icona di notifica in elenco.', severity: 'success' });
    } catch (err) {
      console.error('Errore invio nota:', err);
      setSnackbar({ open: true, message: 'Errore durante l\'invio della nota.', severity: 'error' });
    }
  };

  const handleMarkNoteRead = async () => {
    try {
      await api.put(`/maintenance/${maintenanceId}`, { hasUnreadNote: false });
      setHasUnreadNote(false);
      setSnackbar({ open: true, message: 'Nota segnata come letta.', severity: 'success' });
    } catch (err) {
      console.error('Errore segna nota letta:', err);
      setSnackbar({ open: true, message: 'Errore durante l\'aggiornamento.', severity: 'error' });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const previousStatus = maintenance?.status;

      // Convert laborTime from seconds to minutes for storage
      const laborTimeMinutes = Math.ceil(laborTime / 60);
      
      // Update maintenance record
      const maintenanceData = {
        description: maintenance.description, // Include the description field which is required by the backend
        status,
        priority,
        privateNotes: notes,
        hasUnreadNote,
        laborTime: laborTimeMinutes,
        finalCost: calculateTotalCost(),
        discount: parseFloat(discount) || 0
      };
      
      const res = await api.put(`/maintenance/${maintenanceId}`, maintenanceData);

      if (status === 'Consegnato' && previousStatus !== 'Consegnato') {
        try {
          await openMaintenanceReviewWhatsApp(res.data);
        } catch (e) {
          logger.error('WhatsApp recensioni dopo salvataggio Consegnato:', e);
        }
      }
      
      // Check if status was changed to a completed state
      const completedStatuses = ['Da Chiamare', 'Da Ritirare', 'Sollecitato', 'Consegnato'];
      if (completedStatuses.includes(status) && timerRunning) {
        // If there was an active timer, update the UI state
        setTimerRunning(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        setCurrentWorkSession(null);
        
        // Refresh work sessions to reflect any automatically ended sessions
        fetchWorkSessions();
      }
      
      if (onUpdate) {
        onUpdate(res.data);
      }
      
      handleClose();
    } catch (err) {
      console.error('Error updating maintenance:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCompleteMaintenance = async () => {
    setLoading(true);
    try {
      const res = await api.put(`/maintenance/${maintenanceId}`, {
        ...maintenance,
        status: 'Da Chiamare'
      });
      setMaintenance(res.data);
      setStatus('Da Chiamare');
      
      // If there was an active timer, update the UI state
      if (timerRunning) {
        setTimerRunning(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        setCurrentWorkSession(null);
      }
      
      // Refresh work sessions to reflect any automatically ended sessions
      fetchWorkSessions();
      
      setSnackbar({
        open: true,
        message: 'Manutenzione completata con successo! Stato aggiornato a "Da Chiamare"',
        severity: 'success'
      });
      if (onUpdate) {
        onUpdate(res.data);
      }
    } catch (err) {
      console.error('Error completing maintenance:', err);
      setSnackbar({
        open: true,
        message: `Errore durante il completamento della manutenzione: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (event, newValue) => {
    setSelectedProduct(newValue);
    // Se viene cancellato, resetta anche la quantità
    if (!newValue) {
      setProductQuantity(1);
      setIncludesLabor(true);
    }
  };

  const handleProductSearch = (event, value) => {
    // Handle product search logic
  };

  const handleMechanicSelect = (event, newValue) => {
    if (newValue) {
      if (isMechanicBusy(newValue.id)) {
        setSnackbar({
          open: true,
          message: 'Meccanico occupato: termina prima la sessione attiva in corso',
          severity: 'warning'
        });
        return;
      }
      setSelectedMechanic(newValue);
    }
  };

  const handleMechanicSearch = (event, value) => {
    // Handle mechanic search logic
  };

  const fetchMechanics = async () => {
    try {
      const response = await api.get('/mechanics');
      const mechanics = response.data;
      logger.debug('🔧 Meccanici caricati:', mechanics.length);
      
      if (mechanics.length === 0) {
        logger.warn('⚠️ Nessun meccanico disponibile per l\'azienda attuale.');
        setSnackbar({
          open: true,
          message: 'Nessun meccanico disponibile per l\'azienda attuale',
          severity: 'warning'
        });
      }

      setMechanics(mechanics.map(mechanic => ({
        id: mechanic.id,
        name: mechanic.User ? mechanic.User.name : 'Meccanico non disponibile',
        userId: mechanic.userId,
        hourlyRate: mechanic.hourlyRate || 0
      })));
    } catch (error) {
      logger.error('❌ Errore nel caricamento dei meccanici:', error);
      setSnackbar({
        open: true,
        message: 'Errore nel caricamento dei meccanici',
        severity: 'error'
      });
    }
  };

  const fetchBusyMechanics = async () => {
    try {
      const res = await api.get('/work-sessions/active');
      const ids = new Set((res.data || []).map((s) => s.mechanicId).filter(Boolean));
      setBusyMechanicIds(ids);
    } catch (_) {
      setBusyMechanicIds(new Set());
    }
  };

  const isMechanicBusy = useMemo(() => {
    return (mechanicId) => busyMechanicIds instanceof Set && busyMechanicIds.has(mechanicId);
  }, [busyMechanicIds]);

  const handleDeleteWorkSession = async (sessionId) => {
    if (window.confirm('Sei sicuro di voler eliminare questa sessione di lavoro? Questa azione non può essere annullata.')) {
      try {
        // Utilizza l'endpoint corretto per eliminare le sessioni di lavoro
        await api.delete(`/work-sessions/${sessionId}`);
        setSnackbar({
          open: true,
          message: 'Sessione di lavoro eliminata con successo!',
          severity: 'success'
        });
        // Ricarica le sessioni dopo l'eliminazione
        fetchWorkSessions();
      } catch (err) {
        console.error('Error deleting work session:', err);
        setSnackbar({
          open: true,
          message: `Errore durante l'eliminazione della sessione: ${err.message}`,
          severity: 'error'
        });
      }
    }
  };

  // Add function to handle custom work dialog
  const handleOpenCustomWorkDialog = () => {
    setCustomWork({
      description: '',
      materialsCost: '',
      sellingPrice: ''
    });
    setCustomWorkErrors({});
    setOpenCustomWorkDialog(true);
  };

  const handleCloseCustomWorkDialog = () => {
    setOpenCustomWorkDialog(false);
  };

  const handleOpenAdvancedSearch = () => {
    setSearchResults(products);
    setSelectedItems([]);
    setOpenAdvancedSearch(true);
  };

  const handleCloseAdvancedSearch = () => {
    setOpenAdvancedSearch(false);
    setSearchTerm('');
    setSelectedItems([]);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (term === '') {
      setSearchResults(products);
    } else {
      const filtered = products.filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.code.toLowerCase().includes(term) ||
        (p.description && p.description.toLowerCase().includes(term))
      );
      setSearchResults(filtered);
    }
  };

  const handleToggleItem = (product) => {
    setSelectedItems(prev => {
      const exists = prev.find(item => item.id === product.id);
      if (exists) {
        return prev.filter(item => item.id !== product.id);
      } else {
        return [...prev, {
          ...product,
          quantity: 1,
          includesLabor: false
        }];
      }
    });
  };

  const handleItemQuantityChange = (productId, quantity) => {
    setSelectedItems(prev => prev.map(item =>
      item.id === productId ? { ...item, quantity: parseInt(quantity) || 1 } : item
    ));
  };

  const handleItemLaborChange = (productId, includesLabor) => {
    setSelectedItems(prev => prev.map(item =>
      item.id === productId ? { ...item, includesLabor } : item
    ));
  };

  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    try {
      for (const item of selectedItems) {
        await api.post('/maintenance-items', {
          maintenanceId,
          productId: item.id,
          quantity: item.quantity,
          unitPrice: item.includesLabor ? item.sellingPriceWithLabor : item.sellingPrice,
          includesLabor: item.includesLabor
        });
      }

      setSnackbar({
        open: true,
        message: `${selectedItems.length} articoli aggiunti con successo`,
        severity: 'success'
      });

      await fetchMaintenanceItems();
      handleCloseAdvancedSearch();
    } catch (err) {
      console.error('Error adding items:', err);
      setSnackbar({
        open: true,
        message: 'Errore nell\'aggiungere gli articoli',
        severity: 'error'
      });
    }
  };

  const handleCustomWorkChange = (e) => {
    const { name, value } = e.target;
    setCustomWork(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (customWorkErrors[name]) {
      setCustomWorkErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateCustomWork = () => {
    const errors = {};
    
    // Validazione descrizione
    if (!customWork.description.trim()) {
      errors.description = "La descrizione è obbligatoria";
    }
    
    // Validazione costo materiali
    if (!customWork.materialsCost) {
      errors.materialsCost = "Il costo dei materiali è obbligatorio";
    } else {
      const materialsCost = parseFloat(customWork.materialsCost);
      if (isNaN(materialsCost) || materialsCost < 0) {
        errors.materialsCost = "Inserire un costo valido maggiore o uguale a 0";
      }
    }
    
    // Validazione prezzo di vendita
    if (!customWork.sellingPrice) {
      errors.sellingPrice = "Il prezzo di vendita è obbligatorio";
    } else {
      const sellingPrice = parseFloat(customWork.sellingPrice);
      if (isNaN(sellingPrice) || sellingPrice < 0) {
        errors.sellingPrice = "Inserire un prezzo valido maggiore o uguale a 0";
      }
    }
    
    setCustomWorkErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const checkForQuotes = async () => {
    try {
      const response = await api.get(`/quotes/maintenance/${maintenanceId}`);
      setHasQuotes(response.data && response.data.length > 0);
    } catch (error) {
      console.error('Errore nel controllo preventivi:', error);
      setHasQuotes(false);
    }
  };

  const handleQuoteSave = async (quoteData) => {
    try {
      await api.post('/quotes', quoteData);
      setSnackbar({
        open: true,
        message: 'Preventivo salvato con successo!',
        severity: 'success'
      });
      setOpenQuoteDialog(false);
      // Ricontrolla se ci sono preventivi
      await checkForQuotes();
    } catch (error) {
      console.error('Errore nel salvataggio preventivo:', error);
      setSnackbar({
        open: true,
        message: 'Errore nel salvataggio del preventivo',
        severity: 'error'
      });
    }
  };

  const handleImportQuote = async (quote) => {
    try {
      if (!quote.items || quote.items.length === 0) {
        setSnackbar({
          open: true,
          message: 'Il preventivo selezionato non contiene articoli',
          severity: 'warning'
        });
        return;
      }

      let importedCount = 0;

      // Importa gli articoli del preventivo usando l'API
      for (const item of quote.items) {
        try {
          // Controlla se è un articolo personalizzato o se non ha productId valido
          if (item.isCustom || !item.productId || item.productId === null) {
            // Crea un articolo personalizzato usando l'endpoint specifico
            const unitPrice = parseFloat(item.unitPrice) || 0;
            const costPrice = parseFloat(item.costPrice) || 0;
            const calculatedPurchasePrice = costPrice > 0 ? costPrice : (unitPrice * 0.7);
            const discount = parseFloat(item.discount) || 0;
            
            const payload = {
              description: item.productName,
              quantity: parseInt(item.quantity) || 1, // Passa la quantità dal preventivo
              unitPrice: unitPrice,
              purchasePrice: calculatedPurchasePrice, // Usa il prezzo di costo se disponibile
              discount: discount // Passa lo sconto dal preventivo
            };
            
            logger.debug('🔍 Importazione articolo personalizzato');
            await api.post(`/maintenance-items/${maintenanceId}/custom`, payload);
            importedCount++;
          } else {
            // Trova il prodotto nel magazzino
            const product = products.find(p => p.id === item.productId);
            if (product) {
              // Aggiungi il prodotto tramite API
              // Converti la quantità a intero (il preventivo usa DECIMAL, il backend richiede INT)
              const quantity = Math.floor(parseFloat(item.quantity) || 1);
              const discount = parseFloat(item.discount) || 0;
              
              await api.post('/maintenance-items', {
                maintenanceId,
                productId: item.productId,
                quantity: quantity,
                unitPrice: item.unitPrice,
                discount: discount, // Passa lo sconto dal preventivo
                isLabor: item.isLabor || false,
                includesLabor: item.includesLabor || false
              });
              importedCount++;
            } else {
              // Se il prodotto non esiste nel magazzino, crea un articolo personalizzato
              const unitPrice = parseFloat(item.unitPrice) || 0;
              const costPrice = parseFloat(item.costPrice) || 0;
              const calculatedPurchasePrice = costPrice > 0 ? costPrice : (unitPrice * 0.7);
              const discount = parseFloat(item.discount) || 0;
              
              const payload = {
                description: item.productName,
                quantity: parseInt(item.quantity) || 1, // Passa la quantità dal preventivo
                unitPrice: unitPrice,
                purchasePrice: calculatedPurchasePrice,
                discount: discount // Passa lo sconto dal preventivo
              };
              
              logger.debug('🔍 Importazione articolo personalizzato (fallback)');
              await api.post(`/maintenance-items/${maintenanceId}/custom`, payload);
              importedCount++;
            }
          }
        } catch (itemError) {
          logger.error(`Errore nell'importazione dell'articolo ${item.productName}:`, itemError);
        }
      }

      // Ricarica gli articoli della manutenzione
      await fetchMaintenanceItems();

      setSnackbar({
        open: true,
        message: `Importati ${importedCount} articoli dal preventivo`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Errore nell\'importazione preventivo:', error);
      setSnackbar({
        open: true,
        message: 'Errore nell\'importazione del preventivo',
        severity: 'error'
      });
    }
  };

  const handleAddCustomWork = async () => {
    if (!validateCustomWork()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Preparo solo i campi necessari per il backend
      const customWorkData = {
        description: customWork.description,
        unitPrice: parseFloat(customWork.sellingPrice),
        purchasePrice: parseFloat(customWork.materialsCost)
      };
      
      logger.debug('Invio dati lavorazione personalizzata');
      
      // Invio la richiesta al server per aggiungere una lavorazione personalizzata
      const response = await api.post(
        `/maintenance-items/${maintenanceId}/custom`,
        customWorkData
      );
      
      if (response.data) {
        // Aggiorno la lista delle lavorazioni
        await fetchMaintenanceItems();
        
        // Ripulisco il form
        setCustomWork({
          description: '',
          materialsCost: '',
          sellingPrice: ''
        });
        
        // Chiudo il dialog
        setOpenCustomWorkDialog(false);
        
        setSnackbar({
          open: true,
          message: 'Lavorazione personalizzata aggiunta con successo',
          severity: 'success'
        });
      }
    } catch (error) {
      logger.error('Errore nell\'aggiunta della lavorazione personalizzata:', error);
      
      // Messaggio di errore più specifico se disponibile
      let errorMessage = 'Errore nell\'aggiunta della lavorazione personalizzata';
      if (error.response && error.response.data) {
        if (error.response.data.errors) {
          // Errori di validazione
          errorMessage = error.response.data.errors.map(err => err.msg).join(', ');
        } else if (error.response.data.msg) {
          // Messaggio di errore singolo
          errorMessage = error.response.data.msg;
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!maintenance) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Riparazione - Protocollo #{maintenance.protocolNumber}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Customer and Vehicle Info */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Cliente: {maintenance.Customer ? `${maintenance.Customer.lastName} ${maintenance.Customer.firstName}` : 'N/D'}
                {maintenance.Customer?.phone && (
                  <>
                    {` - ${maintenance.Customer.phone}`}
                    {maintenance.Customer?.id && (
                      <Tooltip title="Visualizza storico manutenzioni">
                        <IconButton
                          size="small"
                          onClick={handleOpenHistoryDialog}
                          color="primary"
                          sx={{ ml: 1, verticalAlign: 'middle' }}
                        >
                          <BuildIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Veicolo: {maintenance.Vehicle ? `${maintenance.Vehicle.brand} ${maintenance.Vehicle.model}` : 'N/D'}
              </Typography>
              <Typography variant="subtitle1">
                Problema: {maintenance.description}
              </Typography>
            </Paper>
          </Grid>
          
          {/* Timer Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              {/* Pulsante Nuovo Preventivo */}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ReceiptIcon />}
                  onClick={() => setOpenQuoteDialog(true)}
                  sx={{ minWidth: 200 }}
                >
                  Nuovo Preventivo
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Crea un preventivo dettagliato con prodotti dal magazzino
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom>
                Sessioni di Lavoro
              </Typography>
              
              {/* Mechanic Selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Autocomplete
                  fullWidth
                  options={mechanics}
                  getOptionLabel={(option) => option ? option.name : ''}
                  value={selectedMechanic}
                  onChange={handleMechanicSelect}
                  onInputChange={handleMechanicSearch}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionDisabled={(option) => isMechanicBusy(option?.id)}
                  renderOption={(props, option) => {
                    const busy = isMechanicBusy(option?.id);
                    return (
                      <li {...props} key={option.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span>{option.name}</span>
                          {busy && (
                            <span style={{ color: '#d32f2f', fontWeight: 700 }}>Occupato</span>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Meccanico"
                      error={!!errors.mechanicId}
                      helperText={errors.mechanicId}
                      required
                      margin="normal"
                    />
                  )}
                />
                {mechanics.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Nessun meccanico disponibile. Contatta l'amministratore per aggiungere dei meccanici.
                  </Alert>
                )}
              </FormControl>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<PlayIcon />}
                  onClick={startTimer}
                  disabled={!selectedMechanic || isMechanicBusy(selectedMechanic?.id)}
                  fullWidth
                >
                  Avvia Sessione
                </Button>
              </Box>
              
              {/* Work Sessions History */}
              {workSessions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Storico Sessioni
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {workSessions.map((session) => {
                      const startTime = new Date(session.startTime);
                      const endTime = session.endTime ? new Date(session.endTime) : null;
                      
                      // Calcola la durata della sessione
                      let duration = 0;
                      if (session.duration) {
                        // Usa la durata salvata per le sessioni completate
                        duration = Math.floor(session.duration / 60); // converti da secondi a minuti
                      } else if (!endTime) {
                        // Calcola la durata in tempo reale per le sessioni in corso
                        const now = new Date();
                        duration = Math.floor((now - startTime) / (1000 * 60)); // differenza in minuti
                      }
                      
                      const isActive = !session.endTime;
                      
                      return (
                        <Card 
                          key={`session-${maintenanceId}-${session.id}`} 
                          sx={{ 
                            width: '100%', 
                            mb: 1,
                            border: isActive ? '2px solid' : '1px solid', 
                            borderColor: isActive ? 'primary.main' : 'divider'
                          }}
                        >
                          <CardContent sx={{ p: 1, pb: '8px !important' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ mr: 1, bgcolor: isActive ? 'primary.main' : 'grey.500', width: 28, height: 28 }}>
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                                <Typography variant="subtitle2" noWrap sx={{ maxWidth: 150 }}>
                                  {session.Mechanic?.User?.name || 'Meccanico'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Chip 
                                  icon={<TimerIcon fontSize="small" />} 
                                  label={isActive ? `${duration} min (in corso)` : `${duration} min`} 
                                  size="small" 
                                  color={isActive ? 'error' : 'primary'} 
                                  variant={isActive ? 'filled' : 'outlined'}
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteWorkSession(session.id)}
                                  title="Elimina sessione"
                                  sx={{ ml: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                                {isActive && (
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => endWorkSession(session.id)}
                                    title="Termina sessione"
                                    sx={{ ml: 0.5 }}
                                  >
                                    <PauseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              {startTime.toLocaleString()} {endTime ? `- ${endTime.toLocaleString()}` : '- In corso'}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Status Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Stato Manutenzione
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="priority-label">Priorità</InputLabel>
                <Select
                  labelId="priority-label"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  label="Priorità"
                >
                  <MenuItem value="Bassa">Bassa</MenuItem>
                  <MenuItem value="Normale">Normale</MenuItem>
                  <MenuItem value="Alta">Alta</MenuItem>
                  <MenuItem value="Urgente">Urgente</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="status-label">Stato</InputLabel>
                <Select
                  labelId="status-label"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  label="Stato"
                >
                  <MenuItem value="In Coda">In Coda</MenuItem>
                  <MenuItem value="In Corso">In Corso</MenuItem>
                  <MenuItem value="Attesa Ricambi">Attesa Ricambi</MenuItem>
                  <MenuItem value="Attesa Cliente">Attesa Cliente</MenuItem>
                  <MenuItem value="Da Chiamare">Da Chiamare</MenuItem>
                  <MenuItem value="Da Ritirare">Da Ritirare</MenuItem>
                  <MenuItem value="Sollecitato">Sollecitato</MenuItem>
                  <MenuItem value="Parcheggio">Parcheggio</MenuItem>
                  <MenuItem value="Consegnato">Consegnato</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">Note Private</Typography>
                  <Tooltip title="Invia nota (segna come da leggere in elenco)">
                    <span>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={handleSendNote}
                        disabled={!notes.trim()}
                      >
                        <SendIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <TextField
                  multiline
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Scrivi una nota..."
                />
                {hasUnreadNote && (
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Checkbox
                        onChange={handleMarkNoteRead}
                        color="primary"
                      />
                    }
                    label="Letto"
                  />
                )}
              </Box>
              
              {/* Total Labor Time */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Tempo Totale Manodopera:
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatTime(laborTime)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          {/* Cost Report Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Riepilogo Costi
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Costo Manodopera
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatTime(laborTime)} ({(laborTime / 3600).toFixed(2)} ore)
                      </Typography>
                      <Typography variant="h6" color="primary">
                        €{calculateLaborCost().toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Costo Materiali (Acquisto + IVA 22%)
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedProducts.filter(p => !p.isLabor).length} articoli
                      </Typography>
                      <Typography variant="h6" color="primary">
                        €{calculateMaterialsCost().toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Spese Generali (20% + IVA 22%)
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Typography variant="h6" color="primary">
                        €{calculateOverheadCost().toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="subtitle1" gutterBottom color="inherit">
                      Totale Complessivo
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Typography variant="h5" color="inherit">
                        €{calculateGrandTotal().toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Parts and Labor Selection */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Lavorazioni svolte e materiali utilizzati
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<ReceiptIcon />}
                    onClick={() => setOpenQuoteViewDialog(true)}
                  >
                    Visualizza Preventivo
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ConstructionIcon />}
                    onClick={handleOpenCustomWorkDialog}
                  >
                    Lavorazione Personalizzata
                  </Button>
                </Box>
              </Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Autocomplete
                      fullWidth
                      options={products}
                      getOptionLabel={(option) => option ? `${option.name} - ${option.code}` : ''}
                      value={selectedProduct}
                      onChange={handleProductSelect}
                      onInputChange={handleProductSearch}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Prodotto"
                          error={!!errors.productId}
                          helperText={errors.productId}
                          required
                          margin="normal"
                        />
                      )}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleOpenAdvancedSearch}
                      sx={{ mt: 2 }}
                      title="Ricerca avanzata prodotti"
                    >
                      <SearchIcon />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label="Quantità"
                    type="number"
                    value={productQuantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Permetti valori vuoti temporaneamente durante la digitazione
                      if (value === '') {
                        setProductQuantity('');
                      } else {
                        const numValue = parseInt(value);
                        if (!isNaN(numValue) && numValue >= 1) {
                          setProductQuantity(numValue);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Quando l'utente esce dal campo, assicurati che ci sia almeno 1
                      if (productQuantity === '' || productQuantity < 1) {
                        setProductQuantity(1);
                      }
                    }}
                    InputProps={{ inputProps: { min: 1, step: 1 } }}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth margin="normal">
                    {selectedProduct && !selectedProduct.isLabor ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={includesLabor}
                            onChange={(e) => setIncludesLabor(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Include manodopera"
                      />
                    ) : (
                      <div style={{ height: 32 }}></div>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddProduct}
                    disabled={!selectedProduct || productQuantity === '' || productQuantity < 1}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Aggiungi
                  </Button>
                </Grid>
              </Grid>
              
              <List sx={{ mt: 2 }}>
                {selectedProducts.length === 0 ? (
                  <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
                    Nessun articolo selezionato
                  </Typography>
                ) : (
                  <>
                    {selectedProducts.map((product) => (
                      <React.Fragment key={product.id || `item-${product.maintenanceItemId}`}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body1" sx={{ color: '#000000' }}>
                                  {product.code} - {product.name}
                                </Typography>
                                {product.isLabor && (
                                  <Chip 
                                    label="Manodopera" 
                                    color="primary" 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                                {product.includesLabor && !product.isLabor && (
                                  <Chip 
                                    label="Include manodopera" 
                                    color="secondary" 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                                {product.isCustomItem && (
                                  <Chip 
                                    label="Articolo personalizzato" 
                                    color="info" 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Quantità:
                                  </Typography>
                                  <TextField
                                    type="number"
                                    value={editingQuantities[product.maintenanceItemId] !== undefined 
                                      ? editingQuantities[product.maintenanceItemId] 
                                      : product.quantity || 1}
                                    onChange={(e) => handleQuantityChange(product.maintenanceItemId, e.target.value)}
                                    onBlur={(e) => handleQuantityBlur(product.maintenanceItemId, e.target.value)}
                                    inputProps={{ min: 1, step: 1 }}
                                    size="small"
                                    sx={{ width: 80 }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.target.blur();
                                      }
                                    }}
                                  />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {(() => {
                                    const unitPrice = parseFloat(product.unitPrice) || 0;
                                    const quantity = editingQuantities[product.maintenanceItemId] !== undefined 
                                      ? (editingQuantities[product.maintenanceItemId] === '' ? 0 : parseInt(editingQuantities[product.maintenanceItemId]) || 0)
                                      : (product.quantity || 0);
                                    const discount = parseFloat(product.discount) || 0;
                                    const itemTotal = unitPrice * quantity;
                                    const discountAmount = discount;
                                    const finalPrice = itemTotal - discountAmount;
                                    return `Prezzo: €${finalPrice.toFixed(2)}${discount > 0 ? ` (sconto €${discount.toFixed(2)})` : ''}`;
                                  })()}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => handleRemoveProduct(product.maintenanceItemId)}>
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 2, gap: 2 }}>
                      <Typography variant="body1">
                        Totale: €{calculateTotalCost().toFixed(2)}
                      </Typography>
                      {discount > 0 && (
                        <Typography variant="h6" fontWeight="bold">
                          Totale con sconto: €{(calculateTotalCost() - (parseFloat(discount) || 0)).toFixed(2)}
                        </Typography>
                      )}
                      {discount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Sconto applicato: €{discount.toFixed(2)}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          label="Sconto (€)"
                          type="number"
                          value={discountInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              setDiscountInput('');
                            } else {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue) && numValue >= 0) {
                                setDiscountInput(value);
                              }
                            }
                          }}
                          onBlur={(e) => {
                            if (discountInput === '' || parseFloat(discountInput) < 0) {
                              setDiscountInput('');
                            } else {
                              setDiscountInput(parseFloat(discountInput).toFixed(2));
                            }
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          size="small"
                          sx={{ width: 120 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>
                          }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={async () => {
                            try {
                              const discountValue = parseFloat(discountInput) || 0;
                              const maintenanceData = {
                                description: maintenance.description,
                                status,
                                privateNotes: notes,
                                hasUnreadNote,
                                laborTime: Math.ceil(laborTime / 60),
                                finalCost: calculateTotalCost(),
                                discount: discountValue
                              };
                              await api.put(`/maintenance/${maintenanceId}`, maintenanceData);
                              
                              // Aggiorna lo sconto e resetta il campo input
                              setDiscount(discountValue);
                              setDiscountInput('');
                              
                              // Ricarica i dati della manutenzione per avere lo sconto aggiornato
                              fetchMaintenance();
                              
                              setSnackbar({
                                open: true,
                                message: 'Sconto salvato con successo',
                                severity: 'success'
                              });
                            } catch (err) {
                              console.error('Error saving discount:', err);
                              setSnackbar({
                                open: true,
                                message: 'Errore nel salvataggio dello sconto',
                                severity: 'error'
                              });
                            }
                          }}
                        >
                          Inserisci
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Annulla</Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckIcon />}
            onClick={handleCompleteMaintenance}
            disabled={loading || !['In Corso', 'Attesa Ricambi', 'Attesa Cliente', 'Parcheggio'].includes(status)}
            sx={{ 
              minWidth: '200px',
              padding: '8px 16px',
              fontSize: '1rem'
            }}
          >
            Manutenzione Conclusa
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </Box>
      </DialogActions>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* MaintenanceDetails Dialog */}
      <MaintenanceDetails
        open={showDetails}
        handleClose={() => setShowDetails(false)}
        maintenanceId={maintenanceId}
        laborCost={calculateLaborCost()}
      />
      {/* Custom Work Dialog */}
      <Dialog open={openCustomWorkDialog} onClose={handleCloseCustomWorkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Aggiungi Lavorazione Personalizzata</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Utilizza questo form per aggiungere una lavorazione personalizzata che non è presente in magazzino
          </Typography>
          
          <TextField
            name="description"
            label="Descrizione lavorazione *"
            fullWidth
            value={customWork.description}
            onChange={(e) => {
              const input = e.target;
              const cursorPosition = input.selectionStart;
              const newValue = e.target.value.toUpperCase();
              
              // Aggiorna lo stato
              handleCustomWorkChange({
                target: {
                  name: e.target.name,
                  value: newValue
                }
              });
              
              // Ripristina la posizione del cursore dopo il re-render
              setTimeout(() => {
                input.setSelectionRange(cursorPosition, cursorPosition);
              }, 0);
            }}
            margin="normal"
            error={!!customWorkErrors.description}
            helperText={customWorkErrors.description}
            required
            multiline
            rows={3}
          />
          
          <TextField
            name="materialsCost"
            label="Costo materiali (€) *"
            fullWidth
            value={customWork.materialsCost}
            onChange={handleCustomWorkChange}
            margin="normal"
            type="number"
            inputProps={{ 
              min: 0, 
              step: 0.01,
              inputMode: 'decimal'
            }}
            error={!!customWorkErrors.materialsCost}
            helperText={customWorkErrors.materialsCost}
            required
          />
          
          <TextField
            name="sellingPrice"
            label="Prezzo di vendita (€) *"
            fullWidth
            value={customWork.sellingPrice}
            onChange={handleCustomWorkChange}
            margin="normal"
            type="number"
            inputProps={{ 
              min: 0, 
              step: 0.01,
              inputMode: 'decimal'
            }}
            error={!!customWorkErrors.sellingPrice}
            helperText={customWorkErrors.sellingPrice}
            required
          />
          
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              * Campi obbligatori
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCustomWorkDialog}>Annulla</Button>
          <Button 
            onClick={handleAddCustomWork} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Aggiungi'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quote Dialog */}
      <QuoteDialog
        open={openQuoteDialog}
        onClose={() => setOpenQuoteDialog(false)}
        onSave={handleQuoteSave}
        customer={maintenance?.Customer}
        vehicle={maintenance?.Vehicle}
        maintenanceData={{
          protocolNumber: maintenance?.protocolNumber,
          priority: maintenance?.priority
        }}
        maintenanceId={maintenanceId}
        saveOnly={true}
      />

      {/* Quote View Dialog */}
      <QuoteViewDialog
        open={openQuoteViewDialog}
        onClose={() => setOpenQuoteViewDialog(false)}
        onImport={handleImportQuote}
        maintenanceId={maintenanceId}
      />

      {/* Advanced Product Search Dialog */}
      <Dialog
        open={openAdvancedSearch}
        onClose={handleCloseAdvancedSearch}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="primary" />
            <Typography variant="h6">Ricerca Avanzata Prodotti</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Cerca per nome, codice o descrizione..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {selectedItems.length} articoli selezionati
          </Typography>

          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {searchResults.map((product) => {
              const isSelected = selectedItems.find(item => item.id === product.id);
              const selectedItem = isSelected ? selectedItems.find(item => item.id === product.id) : null;
              
              return (
                <Paper key={product.id} sx={{ mb: 1, bgcolor: isSelected ? 'action.selected' : 'background.paper' }}>
                  <ListItem>
                    <Checkbox
                      checked={!!isSelected}
                      onChange={() => handleToggleItem(product)}
                    />
                    <ListItemAvatar>
                      <Avatar
                        variant="rounded"
                        src={getProductImage(product.image)}
                        alt={product.name}
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: 'grey.200',
                          '& img': {
                            objectFit: 'contain',
                            p: 0.5
                          }
                        }}
                        imgProps={{
                          onError: (e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
                          }
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ color: '#000000' }}>
                          {product.code} - {product.name}
                        </Typography>
                      }
                      secondary={
                        <Box component="span">
                          <Typography variant="body2" component="span" color="text.secondary">
                            Prezzo: €{product.sellingPrice ? parseFloat(product.sellingPrice).toFixed(2) : '0.00'}
                            {product.assemblyTime > 0 && product.priceWithLabor && (
                              <Typography variant="body2" component="span" color="primary" sx={{ ml: 0.5 }}>
                                {' | '}Con montaggio: €{product.priceWithLabor ? parseFloat(product.priceWithLabor).toFixed(2) : '0.00'}
                              </Typography>
                            )}
                            {' | '}Stock: {product.stock || 0}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  
                  {isSelected && (
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <TextField
                            label="Quantità"
                            type="number"
                            size="small"
                            fullWidth
                            value={selectedItem.quantity}
                            onChange={(e) => handleItemQuantityChange(product.id, e.target.value)}
                            InputProps={{ inputProps: { min: 1 } }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          {!product.isLabor && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedItem.includesLabor}
                                  onChange={(e) => handleItemLaborChange(product.id, e.target.checked)}
                                  color="primary"
                                />
                              }
                              label="Include manodopera"
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdvancedSearch}>
            Annulla
          </Button>
          <Button
            onClick={handleAddSelectedItems}
            variant="contained"
            color="primary"
            disabled={selectedItems.length === 0}
            startIcon={<AddIcon />}
          >
            Aggiungi {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance History Dialog */}
      <Dialog open={openHistoryDialog} onClose={handleCloseHistoryDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="h6" component="span" sx={{ flex: 1 }}>
              Storico Manutenzioni - {maintenance?.Customer ? `${maintenance.Customer.firstName} ${maintenance.Customer.lastName}` : ''}
            </Typography>
            <IconButton
              onClick={() => {
                if (maintenance?.Customer?.id) {
                  fetchMaintenanceHistory(maintenance.Customer.id);
                }
              }}
              color="primary"
              disabled={loadingHistory}
              size="small"
              sx={{ ml: 2 }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : maintenanceHistory.length > 0 ? (
            <List>
              {maintenanceHistory.map((histMaintenance) => (
                <ListItem 
                  key={histMaintenance.id} 
                  button
                  onClick={() => handleMaintenanceClick(histMaintenance.id)}
                  divider
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1">
                          Protocollo #{histMaintenance.protocolNumber}
                        </Typography>
                        <Chip 
                          label={histMaintenance.status} 
                          color={getStatusColor(histMaintenance.status)} 
                          size="small" 
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span">
                          Data: {formatDate(histMaintenance.createdAt)}
                        </Typography>
                        <br />
                        <Typography variant="body2" component="span">
                          {histMaintenance.description && histMaintenance.description.length > 0
                            ? histMaintenance.description.substring(0, 100) + (histMaintenance.description.length > 100 ? '...' : '')
                            : 'Nessuna descrizione'}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center' }}>
              Nessuna manutenzione trovata per questo cliente
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance Details Dialog */}
      {selectedMaintenanceId && (
        <MaintenanceDetails
          open={openMaintenanceDetails}
          onClose={handleCloseMaintenanceDetails}
          maintenanceId={selectedMaintenanceId}
        />
      )}
    </Dialog>
  );
};

export default MaintenanceRepair;